"use client";

import { useCommentPopover } from "@/components/comments/popover-store";
import { cableLabelSwatch } from "@/lib/cable-label-colors";
import { useT } from "@/lib/i18n";
import type { CableSpec } from "@/lib/cable-current";
import { currentNeedsVerification } from "@/lib/cable-current-evidence";
import { formatWire, type UnitSystem } from "@/lib/units";
import { useLayoutEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { MobileDetailsPill } from "./mobile-details-pill";

/** Read-only screen-space details never participate in cable hit testing. */
export function CableHoverDetails({ point, children }: { point: { x: number; y: number }; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const place = () => {
      const margin = 8;
      const gap = 16;
      const { width, height } = el.getBoundingClientRect();
      const left = point.x + gap + width <= window.innerWidth - margin
        ? point.x + gap : point.x - gap - width;
      const top = point.y - gap - height >= margin
        ? point.y - gap - height : point.y + gap;
      el.style.left = `${Math.max(margin, Math.min(left, window.innerWidth - width - margin))}px`;
      el.style.top = `${Math.max(margin, Math.min(top, window.innerHeight - height - margin))}px`;
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [point.x, point.y, children]);

  if (typeof document === "undefined") return null;
  return createPortal(<div
    ref={ref}
    role="tooltip"
    data-cable-hover-details
    className="flex flex-col items-start gap-1"
    style={{ position: "fixed", pointerEvents: "none", zIndex: 1000, maxWidth: "calc(100vw - 16px)", maxHeight: "calc(100vh - 16px)", overflow: "hidden" }}
  >{children}</div>, document.body);
}

/** The static canvas keeps its useful estimate visible without implying that
 * an unresolved current path established this conductor size. */
export function CableSizingValue({ spec, units }: { spec: CableSpec; units: UnitSystem }) {
  const tr = useT();
  const unverified = currentNeedsVerification(spec) || spec.manufacturerInstallationUnverified;
  const title = unverified ? tr(spec.sizingVerificationRequired
    ? "design_profile.sizing_unverified" : spec.manufacturerInstallationUnverified
    ? "cable.manufacturer_installation_unverified" : spec.currentBasis === "provisional-fallback"
    ? "cable.provisional_sizing" : "cable.source_allocation_sizing") : undefined;
  return <span title={title}>
    {formatWire(spec.wire, units)}
    {unverified && <small style={{ color: "var(--color-warning)" }}> · {tr("blueprint.value.verify")}</small>}
  </span>;
}


export function CableDetailsPill({ cableId }: { cableId: string }) {
  return <MobileDetailsPill anchor={{ kind: "cable", id: cableId }} />;
}

/**
 * Sized from a charwidth estimate, not measured text: `charW` must stay generous
 * enough that no glyph run overflows.
 */
export function CableLabelTag({
  text,
  color,
  selected,
  hovered,
  onClick,
  onHoverChange,
  interactive = true,
}: {
  text: string;
  color: string | undefined;
  selected: boolean;
  hovered: boolean;
  onClick: (e: React.MouseEvent) => void;
  onHoverChange: (h: boolean) => void;
  interactive?: boolean;
}) {
  const upper = text.toUpperCase();
  const fontSize = 9.5;
  const letterSpacing = 0.06;
  const charW = 6.6;
  const padLeft = 13;
  const padRight = 9;
  const height = 16;
  const notch = 6;
  const textWidth = upper.length * charW + (upper.length - 1) * fontSize * letterSpacing;
  const width = Math.max(38, padLeft + textWidth + padRight);

  const swatch = cableLabelSwatch(color);
  const bg = swatch.bg;
  const stroke = selected
    ? "var(--color-accent)"
    : hovered
      ? `color-mix(in oklab, ${swatch.edge} 70%, var(--color-fg))`
      : swatch.edge;
  const fg = swatch.fg;

  const d = `M 0 ${height / 2} L ${notch} 0 L ${width} 0 L ${width} ${height} L ${notch} ${height} Z`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      onMouseEnter={interactive ? () => onHoverChange(true) : undefined}
      onMouseLeave={interactive ? () => onHoverChange(false) : undefined}
      onMouseDown={interactive ? (e) => e.stopPropagation() : undefined}
      onClick={interactive ? onClick : undefined}
      style={{
        cursor: interactive ? "pointer" : undefined,
        pointerEvents: interactive ? "auto" : "none",
        maxWidth: "100%",
        overflow: "visible",
        filter:
          hovered || selected
            ? "drop-shadow(0 1.2px 1.4px rgba(60, 30, 0, 0.22))"
            : "drop-shadow(0 0.6px 0.9px rgba(60, 30, 0, 0.16))",
        transition: "filter 0.15s ease",
      }}
    >
      <path
        d={d}
        fill={bg}
        stroke={stroke}
        strokeWidth={0.75}
        strokeLinejoin="round"
      />
      <circle
        cx={notch + 3}
        cy={height / 2}
        r={1.35}
        fill="none"
        stroke={stroke}
        strokeWidth={0.75}
      />
      <text
        x={padLeft + textWidth / 2}
        y={height / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fill={fg}
        style={{
          fontFamily: "var(--font-sans)",
          fontSize,
          fontWeight: 600,
          letterSpacing: `${letterSpacing}em`,
          fontVariantNumeric: "normal",
          // `normal`: the body's tabular-nums splits numerals oddly in a
          // letterspaced uppercase run.
        }}
      >
        {upper}
      </text>
    </svg>
  );
}

export function CableCommentChip({ count, cableId }: { count: number; cableId: string }) {
  const toggle = useCommentPopover((s) => s.toggle);
  return (
    <button
      type="button"
      data-comment-chip
      aria-label={`${count} comment${count === 1 ? "" : "s"} — open`}
      onClick={(e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        toggle({ kind: "cable", cableId }, rect);
      }}
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        height: 18,
        padding: "0 6px",
        borderRadius: 9,
        background: "var(--color-surface)",
        border: "1px solid var(--color-border-strong)",
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        lineHeight: 1,
        fontWeight: 500,
        letterSpacing: "-0.01em",
        color: "var(--color-fg)",
        boxShadow: "0 1px 2px hsl(30 8% 10% / 0.06)",
        pointerEvents: "auto",
        cursor: "pointer",
      }}
      className="hover:bg-[var(--color-surface-2)] transition-colors"
    >
      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      {count}
    </button>
  );
}
